import React, { useCallback, useContext, useEffect, useState } from "react";
import { Card, Empty, Input, notification } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SessionManager from "@Core/session";
import { getAddressGroupTag, getCreditGroupTag, getLicenseGroupTag, TagType } from "@Components/groups/TagProperties";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import { QueryKeys, SystemContext } from "@Constants";
import UserGroupService from "@Services/UserGroupService";
import AddressService from "@Services/AddressService";
import { IAddressDetailsPaged } from "@Models/IAddressDetails";
import LicenseService from "@Services/LicenseService";
import { ILicenseCreationDetails, ILicenseDetailsPaged } from "@Models/ILicenseDetails";
import CreditService from "@Services/CreditService";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import {
    buildGroupList,
    getListStateGroup,
    IGroupEntry,
    IListState,
    INavState,
    ITag,
    updateCurrentGroup,
} from "@Components/groups/GroupUtils";
import { renderGroupHeader, renderGroups, renderSearchHeader } from "@Components/groups/GroupRenderers";
import { SkeletonRows } from "@Components/shared/SkeletonRows";
import { MainContext } from "@Context/provider";
import "@Styles/pages/groupsPage.scss";
import { getSystemId, getUserSignature, hasCapability, refreshUserSignature } from "@Utils";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import UserSearchList from "@Components/groups/UserSearchList";
import { IRolePayload } from "@Models/IRoleDetails";
import appQueryClient from "@AppQueryClient";

const GroupsList: React.FC = () => {
    const queryClient = useQueryClient();
    const { ui: uiState, setCurrentGroup, setCurrentUser } = useContext(MainContext);
    const [cachedGroup, setCachedGroup] = useState<string>(null);
    const [navState, setNavState] = useState<INavState>({ currentLevel: -1, currentGroupId: "" });
    const [listState, setListState] = useState<IListState>({ groups: [] });
    const [groupTags, setGroupTags] = useState<ITag[]>([]);
    const [enabled, setEnabled] = useState<boolean>(false);
    const [searchActive, setSearchActive] = useState<boolean>(false);
    const [searching, setSearching] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");

    /* ---------- Queries & Mutations ------------- */

    /**
     * Get all groups.
     */
    const groupsQuery = useQuery<IUserGroupDetails[]>(
        [QueryKeys.UserGroups],
        async () => {
            const getSystem = hasCapability("UserGroup/GetSystem", SystemContext);
            const service = new UserGroupService();
            const groupsResult = getSystem
                ? await service.getSystem()
                : await service.getUser(SessionManager.getServiceUser()?.userId);

            if (!groupsResult.hasErrors) {
                return groupsResult.value;
            }
            return [];
        },
        {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser()?.userId && enabled,
        }
    );

    /**
     * Create group.
     */
    const groupCreateGroup = useMutation(
        async (group: IUserGroupDetails) => {
            const service = new UserGroupService();
            return await service.create(group);
        },
        {
            onSuccess: async (res) => {
                if (res?.value?.id) {
                    // Refresh the user signature to ensure the new group is added to the user's signature.
                    await refreshUserSignature();

                    notification.success({ message: "New group created." });
                    await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                    await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                } else {
                    notification.error({ message: "Error creating new group." });
                }
            },
        }
    );

    /**
     * Delete group.
     */
    const groupDeleteGroup = useMutation(
        async (group: IGroupEntry) => {
            notification.info({ message: "Deleting group." });
            handleGroupBack(null);
            const service = new UserGroupService();
            const res = await service.delete(group.groupId);
            if (!res.hasErrors) {
                await resetQueries();
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group and all child groups deleted." });
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            },
        }
    );

    /**
     * Get group addresses.
     */
    const addressQueryGroup = useQuery(
        [QueryKeys.UserGroupAddresses, cachedGroup],
        async () => {
            if (cachedGroup === null || cachedGroup === "") return [];
            const service = new AddressService();
            const fetchedAddresses = await service.getGroup([cachedGroup]);

            if (fetchedAddresses.hasErrors) return [];
            const newAddresses: IAddressDetailsPaged[] = fetchedAddresses.value.map((x) => {
                return { ...x, page: 0 };
            });
            return newAddresses;
        },
        {
            placeholderData: [],
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!cachedGroup && !searchActive,
        }
    );
    const { data: addresses } = addressQueryGroup;

    /**
     * Create group address.
     */
    const addressCreateGroup = useMutation(
        async (address: IAddressDetailsPaged) => {
            const service = new AddressService();
            const newAddress = await service.createGroup(address.ownerId, address, address.instanceId);
            if (!newAddress.hasErrors) {
                setCurrentGroup({ instance: address.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group address created." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupAddresses]);
            },
        }
    );

    /**
     * Delete group address.
     */
    const addressDeleteGroup = useMutation(
        async (address: IAddressDetailsPaged) => {
            const service = new AddressService();
            const deleteAddress = await service.delete(address.addressId);
            if (!deleteAddress.hasErrors) {
                setCurrentGroup({ instance: address.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group address deleted." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupAddresses]);
            },
        }
    );

    /**
     * Get licenses for groups.
     */
    const licenseQueryGroup = useQuery(
        [QueryKeys.UserGroupLicenses, cachedGroup],
        async () => {
            if (cachedGroup === null || cachedGroup === "") return [];
            const service = new LicenseService();

            const fetchedLicenses = await service.getGroup([cachedGroup]);
            if (fetchedLicenses.hasErrors) return [];

            const newLicenses: ILicenseDetailsPaged[] = fetchedLicenses.value.map((x) => {
                return { ...x, page: 0 };
            });

            return newLicenses;
        },
        {
            placeholderData: [],
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!cachedGroup && !searchActive,
        }
    );
    const { data: licenses } = licenseQueryGroup;

    /**
     * Create group license.
     */
    const licenseCreateGroup = useMutation(
        async (licenseDetails: ILicenseCreationDetails) => {
            notification.info({ message: "Creating license..." });
            const service = new LicenseService();
            const newLicense = await service.create(licenseDetails);
            if (!newLicense.hasErrors) {
                setCurrentGroup({ instance: licenseDetails.instanceId });
                await resetQueries();
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group license created." });
            },
        }
    );

    /**
     * Get credits for groups.
     */
    const creditQueryGroup = useQuery(
        [QueryKeys.UserGroupCredits, cachedGroup],
        async () => {
            if (cachedGroup === null || cachedGroup === "") return [];
            const service = new CreditService();
            const fetchedCredits = await service.getGroup([cachedGroup]);
            if (fetchedCredits.hasErrors) return [];
            const newCredits: ICreditDetailsPaged[] = fetchedCredits.value.map((x) => {
                return { ...x, page: 0 };
            });
            return newCredits;
        },
        {
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            enabled: !!cachedGroup && !searchActive,
        }
    );
    const { data: credits } = creditQueryGroup;

    /**
     * Create group credit.
     */
    const creditCreateGroup = useMutation(
        async (credit: ICreditDetailsPaged) => {
            const service = new CreditService();
            const newCredit = await service.createGroup(credit.ownerId, credit, credit.instanceId);
            if (!newCredit.hasErrors) {
                setCurrentGroup({ instance: credit.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group credit account created." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupCredits]);
                await queryClient.invalidateQueries([QueryKeys.UserGroupAddresses]);
            },
        }
    );

    /**
     * Delete group credit.
     */
    const creditDeleteGroup = useMutation(
        async (credit: ICreditDetailsPaged) => {
            const service = new CreditService();
            const deleteCredit = await service.delete([credit.creditId]);
            if (!deleteCredit.hasErrors) {
                setCurrentGroup({ instance: credit.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group credit account deleted." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupCredits]);
            },
        }
    );

    /* ---------- Handlers ------------- */

    /**
     * Perform mutations based upon which option is selected from the group assignment menu.
     * @param type
     * @param params
     */
    const addAction = (type: TagType, params: any) => {
        switch (type) {
            case TagType.AddressGroup:
                addressCreateGroup.mutate(params);
                break;
            case TagType.LicenseGroup:
                licenseCreateGroup.mutate(params);
                break;
            case TagType.CreditGroup:
                creditCreateGroup.mutate(params);
                break;
            case TagType.Group:
                groupCreateGroup.mutate(params);
                break;
        }
    };

    /**
     * Delete a group and all child groups.
     * @param group
     */
    const deleteAction = (group: IGroupEntry) => {
        groupDeleteGroup.mutate(group);
    };

    /**
     * Handle selection of a new group item.
     * @param groupEntry
     */
    const handleGroupSelect = (groupEntry: IGroupEntry) => {
        // Update the ui state with the selected group

        setCurrentGroup({ instance: groupEntry.groupId });
        setCurrentUser(null);
        setCachedGroup(groupEntry.groupId);

        // Set new group visibility to true in the list state
        const newListState = { ...listState };
        listState.groups.forEach((group, index) => {
            newListState.groups[index].visibility =
                group.groupId === groupEntry.groupId ||
                group.groupId === navState.currentGroupId ||
                uiState?.currentGroup?.instance === groupEntry.groupId;
        });
        setListState(newListState);

        // Update the nav state
        let newNavState = { ...navState };
        newNavState = updateCurrentGroup(newNavState, groupEntry.groupId);
        newNavState.currentLevel++;
        setNavState(newNavState);
    };

    /**
     * Handle group header back button click.
     */
    const handleGroupBack = (e) => {
        e?.stopPropagation();
        if (navState.currentLevel === -1) return;
        let newNavState = { ...navState };
        const group = getListStateGroup(listState, navState.currentGroupId);
        newNavState = updateCurrentGroup(newNavState, group !== null ? group.parentGroupId : "");
        newNavState.currentLevel = Math.max(newNavState.currentLevel - 1, -1);
        setNavState(newNavState);
        setCurrentUser(null);
        if (group !== null) {
            setCurrentGroup(group.parentGroupId === getSystemId() ? null : { instance: group.parentGroupId });
        } else {
            setCurrentGroup(null);
        }
    };

    /**
     * Update the ui state with the selected user
     * @param userId
     */
    const handleUserSelect = useCallback(
        (userId: string) => {
            if (userId !== uiState.currentUser?.instance) setCurrentUser({ instance: userId });
        },
        [uiState.currentUser?.instance]
    );

    /**
     * Update the visibility of each group to display only the current group.
     * Only to be called after a css transition has ended.
     */
    const sortGroupVisibility = async () => {
        const newListState = { ...listState };
        listState.groups.forEach((group, index) => {
            newListState.groups[index].visibility = group.groupId === navState.currentGroupId;
        });
        setListState(newListState);
        await refetchQueries();
    };

    /**
     * Update the searching state, for use with group header spinner.
     * @param enabled
     */
    const toggleSearching = useCallback((isSearchingEnabled: boolean) => {
        setSearching(isSearchingEnabled);
    }, []);

    const hasFetchedTags = addressQueryGroup.isFetched && licenseQueryGroup.isFetched && creditQueryGroup.isFetched;
    const isLoadingTags =
        !hasFetchedTags && (addressQueryGroup.isLoading || licenseQueryGroup.isLoading || creditQueryGroup.isLoading);

    /***
     * Set the search term in state, to be passed dow to the user search list.
     */
    const applySearchTerm = async (term) => {
        setAppliedSearchTerm(term);
    };

    /**
     * Trigger a search a short time after last keypress.
     */
    const debouncePromise = useCallback(AwesomeDebouncePromise(applySearchTerm, 800), []);

    /**
     * Trigger a search on input change
     * @param term
     */
    const onInputChange = async (term: string) => {
        setSearchTerm(term);
        setSearchActive(term.length > 0);
        if (searchActive) {
            setCurrentGroup({ instance: getSystemId() });
            setCurrentUser(null);
        } else {
            setCurrentGroup({ instance: cachedGroup });
            setCurrentUser(null);
        }
        await debouncePromise(term);
    };

    /**
     * Reset all group and user queries.
     */
    const resetQueries = async () => {
        if (!enabled) return;
        await groupsQuery.refetch();
        await queryClient.resetQueries([QueryKeys.UserGroupAddresses]);
        await queryClient.resetQueries([QueryKeys.UserGroupCredits]);
        await queryClient.resetQueries([QueryKeys.UserGroupLicenses]);
        await queryClient.resetQueries([QueryKeys.GroupUserRoles]);
        await queryClient.resetQueries([QueryKeys.GroupUserAddresses]);
        await queryClient.resetQueries([QueryKeys.GroupUserCredits]);
        await queryClient.resetQueries([QueryKeys.GroupUserLicenses]);
    };

    const refetchQueries = async () => {
        await queryClient.invalidateQueries([QueryKeys.UserGroupAddresses]);
        await queryClient.invalidateQueries([QueryKeys.UserGroupCredits]);
        await queryClient.invalidateQueries([QueryKeys.UserGroupLicenses]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserRoles]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserCredits]);
        await queryClient.invalidateQueries([QueryKeys.GroupUserLicenses]);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setAppliedSearchTerm("");
        setSearchActive(false);
    };

    const groups = renderGroups(
        listState,
        navState,
        handleGroupSelect,
        handleUserSelect,
        sortGroupVisibility,
        searchActive
    );

    /**
     * This ensures there is some initial data to render.
     */
    useEffect(() => {
        if (!groupsQuery?.data?.length) return;
        const list = buildGroupList(navState, groupsQuery?.data);
        setListState(list);
    }, [groupsQuery?.data]);

    /**
     * Reset all group and user queries on component mount
     */
    useEffect(() => {
        resetQueries().then();
        setCurrentGroup(null);
        setCurrentUser(null);
    }, []);

    useEffect(() => {
        const allRoles = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
        const userRoles = getUserSignature();
        setEnabled(allRoles !== undefined && allRoles !== null && userRoles !== undefined && userRoles !== null);
    }, [appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles])]);

    /**
     * Rebuild the list state when fetching or updating groups, users or modules completes.
     */
    useEffect(() => {
        if (hasFetchedTags) {
            const tags: ITag[] = [];
            addresses.forEach((address) => {
                let enableDelete = true;
                if (credits.length > 0) {
                    if (
                        credits?.findIndex(
                            (x) => x.contextId === address.contextId && x.instanceId === address.instanceId
                        )
                    ) {
                        enableDelete = false;
                    }
                }
                tags.push(
                    getAddressGroupTag(address.instanceId, address, enableDelete, () =>
                        addressDeleteGroup.mutate(address)
                    )
                );
            });
            licenses.forEach((license) => {
                tags.push(
                    getLicenseGroupTag(license.instanceId, license, null) // Licenses cannot be deleted via their tag
                );
            });
            credits.forEach((credit) => {
                tags.push(getCreditGroupTag(credit.instanceId, credit, () => creditDeleteGroup.mutate(credit)));
            });
            setGroupTags(tags);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasFetchedTags]);

    return (
        <Card id="groups-list" key="groups-view-list" className={"newUI-raised newUI-groups-list"}>
            {searchActive
                ? renderSearchHeader(clearSearch, searching)
                : renderGroupHeader(
                      listState,
                      navState,
                      groupsQuery.isFetching,
                      isLoadingTags,
                      handleGroupBack,
                      addAction,
                      deleteAction,
                      groupTags,
                      addresses,
                      licenses,
                      credits
                  )}
            <div id="users-search" key={"users-search"}>
                <Input
                    value={searchTerm}
                    placeholder={"Search all users"}
                    style={{ width: "100%" }}
                    onChange={(e) => onInputChange(e.target.value)}
                    allowClear={true}
                />
            </div>
            <div id="groups-list-wrapper" key="groups-view-wrapper" style={{ height: "calc(100vh - 240px)" }}>
                {
                    <div
                        key={"level-search"}
                        id={"level-search"}
                        className="groups-list-list"
                        style={{ display: searchActive ? "block" : "none" }}
                    >
                        <UserSearchList
                            currentGroup={navState.currentLevel !== -1 ? navState.currentGroupId : undefined}
                            groups={listState.groups}
                            toggleSearching={toggleSearching}
                            searchActive={searchActive}
                            searchTerm={appliedSearchTerm}
                            handleUserSelect={handleUserSelect}
                        />
                    </div>
                }
                {groupsQuery.isFetching ? (
                    <SkeletonRows />
                ) : groups.length > 0 ? (
                    <div style={{ display: searchActive ? "none" : "block" }}>{groups}</div>
                ) : (
                    <Empty description="No groups found." />
                )}
            </div>
        </Card>
    );
};
export default GroupsList;
